import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { onDocumentLoaded, changeMetaThemeColor, setHeaderMenuStyle } from '@theme/utilities';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 * @property {HTMLButtonElement | undefined} mobilePurchaseButton - Product-only projected purchase button
 * @property {HTMLElement | undefined} mobilePurchaseLabel - Projected purchase label
 * @property {HTMLElement | undefined} mobilePurchasePrice - Projected selected-variant price
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

const MOBILE_BREAKPOINT = '(max-width: 749px)';
const MOBILE_SCROLL_DEAD_ZONE = 6;
const MOBILE_HIDE_SCROLL_THRESHOLD = 64;
const MOBILE_TOP_REVEAL_THRESHOLD = 24;

/**
 * @typedef {Object} StickyPurchaseState
 * @property {string} productId
 * @property {boolean} active
 * @property {boolean} available
 * @property {string} label
 * @property {string} price
 * @property {number} quantity
 */

/** @typedef {CustomEvent<StickyPurchaseState>} StickyPurchaseStateEvent */

/**
 * @typedef {HTMLElement & {
 *   handleAddToCartClick?: (event?: Event) => Promise<void>,
 *   getPurchaseState?: () => StickyPurchaseState
 * }} StickyPurchaseController
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up'
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * Last meaningful mobile scroll position used to determine floating-header direction.
   * @type {number}
   */
  #lastMobileScrollTop = 0;

  /**
   * Whether the floating mobile header is currently translated out of the viewport.
   * @type {boolean}
   */
  #mobileFloatingHidden = false;

  /**
   * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
   * @type {number | null}
   */
  #timeout = null;

  /**
   * RAF ID for scroll handler throttling
   * @type {number | null}
   */
  #scrollRafId = null;

  /** @type {StickyPurchaseState | null} */
  #mobilePurchaseState = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry || !entry.borderBoxSize[0]) return;

    // The initial height is calculated using the .offsetHeight property, which returns an integer.
    // We round to the nearest integer to avoid unnecessaary reflows.
    const roundedHeaderHeight = Math.round(entry.borderBoxSize[0].blockSize);
    document.body.style.setProperty('--header-height', `${roundedHeaderHeight}px`);

    // Check if the menu drawer should be hidden in favor of the header menu
    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }

    // A viewport change can move the theme between the mobile floating layout and the desktop layout.
    // Always restore the normal desktop transform immediately when that happens.
    this.#syncMobileFloatingViewport();
  });

  /**
   * Observes the header while scrolling the viewport to track when its actively sticky
   * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        this.dataset.stickyState = isIntersecting ? 'inactive' : 'active';
        if (this.dataset.themeColor) changeMetaThemeColor(this.dataset.themeColor);
      } else {
        this.#offscreen = !isIntersecting || this.dataset.stickyState === 'active';
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };

  /**
   * Handles the overflow minimum event from the header menu
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   */
  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.#menuDrawerHiddenWidth = window.innerWidth;
    } else {
      this.#menuDrawerHiddenWidth = null;
    }
    setHeaderMenuStyle();
  }

  /**
   * Whether the viewport is currently using the floating mobile header layout.
   *
   * @returns {boolean}
   */
  #isMobileFloatingHeader() {
    return this.dataset.mobileFloating === 'true' && window.matchMedia(MOBILE_BREAKPOINT).matches;
  }

  /**
   * Return the transform that moves the floating header completely beyond the viewport edge.
   * The direction follows the configured floating-header position.
   *
   * @returns {string}
   */
  #getMobileHiddenTransform() {
    if (this.dataset.mobilePosition === 'bottom') {
      return 'translate3d(0, calc(100% + var(--mobile-header-margin-bottom) + env(safe-area-inset-bottom) + 8px), 0)';
    }

    return 'translate3d(0, calc(-100% - var(--mobile-header-margin-top) - env(safe-area-inset-top) - 8px), 0)';
  }

  /**
   * Move the floating mobile header in or out of view.
   *
   * @param {boolean} hidden
   * @param {boolean} [immediate=false]
   */
  #setMobileFloatingHidden(hidden, immediate = false) {
    if (!this.#isMobileFloatingHeader()) {
      this.#mobileFloatingHidden = false;
      delete this.dataset.mobileScrollState;
      this.style.removeProperty('transform');
      this.style.removeProperty('pointer-events');
      this.style.removeProperty('will-change');
      this.style.removeProperty('transition');
      return;
    }

    if (hidden && this.dataset.purchaseMode === 'true') hidden = false;

    if (this.#mobileFloatingHidden === hidden && !immediate) return;

    this.#mobileFloatingHidden = hidden;
    this.dataset.mobileScrollState = hidden ? 'hidden' : 'visible';

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const transition = immediate || reduceMotion
      ? 'none'
      : 'transform var(--animation-speed) var(--animation-easing), opacity var(--animation-speed) var(--animation-easing)';

    this.style.setProperty('transition', transition);
    this.style.setProperty('will-change', 'transform');
    this.style.setProperty('transform', hidden ? this.#getMobileHiddenTransform() : 'translate3d(0, 0, 0)');

    if (hidden) {
      this.style.setProperty('pointer-events', 'none');
    } else {
      this.style.removeProperty('pointer-events');
    }
  }

  /**
   * Keep the header in the correct state when the viewport crosses the mobile breakpoint.
   */
  #syncMobileFloatingViewport() {
    if (!this.#isMobileFloatingHeader()) {
      this.#setMobileFloatingHidden(false, true);
      this.#applyMobilePurchaseState();
      return;
    }

    this.#lastMobileScrollTop = Math.max(0, document.scrollingElement?.scrollTop ?? 0);

    if (this.#lastMobileScrollTop <= MOBILE_TOP_REVEAL_THRESHOLD) {
      this.#setMobileFloatingHidden(false, true);
    }

    this.#applyMobilePurchaseState();
  }

  /**
   * Whether this header owns the integrated mobile purchase presentation.
   *
   * @returns {boolean}
   */
  #isMobilePurchaseApplicable() {
    return this.#isMobileFloatingHeader() && this.dataset.mobilePosition === 'bottom';
  }

  /**
   * Find the sticky controller for a projected product without taking over its form authority.
   *
   * @param {string} productId
   * @returns {StickyPurchaseController | null}
   */
  #getStickyPurchaseController(productId) {
    const controllers = /** @type {NodeListOf<StickyPurchaseController>} */ (
      document.querySelectorAll('sticky-add-to-cart')
    );

    return Array.from(controllers).find((controller) => controller.dataset.productId === productId) ?? null;
  }

  /**
   * Hydrate from an already-connected sticky controller after the event listener
   * is installed. The sticky module loads before the header module and may have
   * published its initial snapshot before this component was upgraded.
   */
  #hydrateMobilePurchaseState() {
    const button = this.refs.mobilePurchaseButton;
    if (!(button instanceof HTMLButtonElement)) return;

    const controller = this.#getStickyPurchaseController(button.dataset.productId || '');
    if (!controller?.getPurchaseState) return;

    const state = controller.getPurchaseState();
    if (state.productId !== button.dataset.productId) return;

    this.#mobilePurchaseState = state;
    if (this.dataset.mobileFloating === 'true' && this.dataset.mobilePosition === 'bottom') {
      controller.dataset.mobileHeaderIntegrated = 'true';
    }
    this.#applyMobilePurchaseState();
  }

  /**
   * Apply the latest authoritative sticky projection to the product-only header button.
   */
  #applyMobilePurchaseState() {
    const button = this.refs.mobilePurchaseButton;
    const label = this.refs.mobilePurchaseLabel;
    const price = this.refs.mobilePurchasePrice;
    const state = this.#mobilePurchaseState;

    if (!(button instanceof HTMLButtonElement)) {
      this.dataset.purchaseMode = 'false';
      return;
    }

    if (state) {
      if (label instanceof HTMLElement && state.label) label.textContent = state.label;
      if (price instanceof HTMLElement && state.price) price.textContent = state.price;
      button.dataset.productId = state.productId;
      button.setAttribute('aria-label', [state.label, state.price].filter(Boolean).join(', '));
    }

    // The integrated mobile CTA is persistent. The controller's active flag only
    // controls the standalone sticky bar's viewport and footer visibility.
    const purchaseMode = Boolean(state?.available && this.#isMobilePurchaseApplicable());
    button.disabled = !purchaseMode;
    button.hidden = !purchaseMode;
    this.dataset.purchaseMode = purchaseMode ? 'true' : 'false';

    if (purchaseMode) this.#setMobileFloatingHidden(false);
  }

  /**
   * Consume the sticky component's presentation-only state snapshot.
   *
   * @param {StickyPurchaseStateEvent} event
   */
  #handleStickyPurchaseState = (event) => {
    const button = this.refs.mobilePurchaseButton;
    if (!(button instanceof HTMLButtonElement)) return;
    if (event.detail.productId !== button.dataset.productId) return;

    this.#mobilePurchaseState = event.detail;

    const controller = this.#getStickyPurchaseController(event.detail.productId);
    if (controller && this.dataset.mobileFloating === 'true' && this.dataset.mobilePosition === 'bottom') {
      controller.dataset.mobileHeaderIntegrated = 'true';
    }

    this.#applyMobilePurchaseState();
  };

  /**
   * Delegate the visible header CTA to the matching sticky controller and native product form.
   *
   * @param {Event} event
   */
  handleMobilePurchaseClick = (event) => {
    const button = event.target instanceof HTMLButtonElement ? event.target : this.refs.mobilePurchaseButton;
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;

    const controller = this.#getStickyPurchaseController(button.dataset.productId || '');
    if (!controller?.handleAddToCartClick) {
      this.#mobilePurchaseState = null;
      this.#applyMobilePurchaseState();
      return;
    }

    void controller.handleAddToCartClick(event);
  };

  /**
   * Update the floating mobile header from scroll direction.
   * Small movements are accumulated until they clear the dead zone, which prevents touch jitter
   * from rapidly toggling the navigation bar.
   *
   * @param {number} scrollTop
   */
  #updateMobileFloatingScrollState(scrollTop) {
    if (!this.#isMobileFloatingHeader()) {
      this.#setMobileFloatingHidden(false, true);
      return;
    }

    const currentScrollTop = Math.max(0, scrollTop);

    if (this.dataset.purchaseMode === 'true') {
      this.#lastMobileScrollTop = currentScrollTop;
      this.#setMobileFloatingHidden(false);
      return;
    }

    // Keep the navigation visible around the top of the page regardless of tiny overscroll changes.
    if (currentScrollTop <= MOBILE_TOP_REVEAL_THRESHOLD) {
      this.#lastMobileScrollTop = currentScrollTop;
      this.#setMobileFloatingHidden(false);
      return;
    }

    const delta = currentScrollTop - this.#lastMobileScrollTop;

    if (Math.abs(delta) < MOBILE_SCROLL_DEAD_ZONE) return;

    if (delta > 0 && currentScrollTop > MOBILE_HIDE_SCROLL_THRESHOLD) {
      // User is deliberately scrolling down: move the floating header beyond its nearest viewport edge.
      this.#setMobileFloatingHidden(true);
    } else if (delta < 0) {
      // Any deliberate upward scroll should make navigation immediately available again.
      this.#setMobileFloatingHidden(false);
    }

    this.#lastMobileScrollTop = currentScrollTop;
  }

  #handleWindowScroll = () => {
    if (this.#scrollRafId !== null) return;

    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      this.#updateScrollState();
    });
  };

  #updateScrollState = () => {
    const scrollTop = document.scrollingElement?.scrollTop ?? 0;

    // Floating mobile navigation is independent of the desktop sticky-header mode.
    this.#updateMobileFloatingScrollState(scrollTop);

    const stickyMode = this.getAttribute('sticky');
    if (!this.#offscreen && stickyMode !== 'always') return;

    const headerTop = this.getBoundingClientRect().top;
    const isScrollingUp = scrollTop < this.#lastScrollTop;
    const isAtTop = headerTop >= 0;

    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (stickyMode === 'always') {
      if (isAtTop) {
        this.dataset.scrollDirection = 'none';
      } else if (isScrollingUp) {
        this.dataset.scrollDirection = 'up';
      } else {
        this.dataset.scrollDirection = 'down';
      }

      this.#lastScrollTop = scrollTop;
      return;
    }

    if (isScrollingUp) {
      if (isAtTop) {
        // reset sticky state when header is scrolled up to natural position
        this.#offscreen = false;
        this.dataset.stickyState = 'inactive';
        this.dataset.scrollDirection = 'none';
      } else {
        // show sticky header when scrolling up
        this.dataset.stickyState = 'active';
        this.dataset.scrollDirection = 'up';
      }
    } else if (this.dataset.stickyState === 'active') {
      this.dataset.scrollDirection = 'none';

      this.dataset.stickyState = 'idle';
    } else {
      this.dataset.scrollDirection = 'none';
      this.dataset.stickyState = 'idle';
    }

    this.#lastScrollTop = scrollTop;
  };

  connectedCallback() {
    super.connectedCallback();
    if (this.#abortController.signal.aborted) this.#abortController = new AbortController();
    this.#resizeObserver.observe(this);
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);

    const stickyMode = this.getAttribute('sticky');
    if (stickyMode) {
      this.#observeStickyPosition(stickyMode === 'always');
    }

    const tracksStickyScroll = stickyMode === 'scroll-up' || stickyMode === 'always';
    const tracksMobileFloatingScroll = this.dataset.mobileFloating === 'true';

    if (tracksStickyScroll || tracksMobileFloatingScroll) {
      document.addEventListener('scroll', this.#handleWindowScroll, { passive: true });
    }

    this.#lastMobileScrollTop = Math.max(0, document.scrollingElement?.scrollTop ?? 0);
    this.#setMobileFloatingHidden(false, true);
    document.addEventListener(ThemeEvents.stickyAddToCartState, this.#handleStickyPurchaseState, {
      signal: this.#abortController.signal,
    });
    this.#hydrateMobilePurchaseState();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    document.removeEventListener('scroll', this.#handleWindowScroll);
    if (this.#scrollRafId !== null) {
      cancelAnimationFrame(this.#scrollRafId);
      this.#scrollRafId = null;
    }
    this.#abortController.abort();
    this.#setMobileFloatingHidden(false, true);
    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentLoaded(() => {
  const header = document.querySelector('header-component');
  const headerGroup = document.querySelector('#header-group');

  // Note: Initial header heights are set via inline script in theme.liquid
  // This ResizeObserver handles dynamic updates after page load

  // Update header group height on resize of any child
  if (headerGroup) {
    const resizeObserver = new ResizeObserver((entries) => {
      const headerGroupHeight = entries.reduce((totalHeight, entry) => {
        if (
          entry.target !== header ||
          (header.hasAttribute('transparent') && header.parentElement?.nextElementSibling)
        ) {
          return totalHeight + (entry.borderBoxSize[0]?.blockSize ?? 0);
        }
        return totalHeight;
      }, 0);
      // The initial height is calculated using the .offsetHeight property, which returns an integer.
      // We round to the nearest integer to avoid unnecessaary reflows.
      const roundedHeaderGroupHeight = Math.round(headerGroupHeight);
      document.body.style.setProperty('--header-group-height', `${roundedHeaderGroupHeight}px`);
    });

    if (header instanceof HTMLElement) {
      resizeObserver.observe(header);
    }

    // Observe all children of the header group
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element instanceof HTMLElement) {
        resizeObserver.observe(element);
      }
    }

    // Also observe the header group itself for child changes
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          // Re-observe all children when the list changes
          const children = headerGroup.children;
          for (let i = 0; i < children.length; i++) {
            const element = children[i];
            if (element instanceof HTMLElement) {
              resizeObserver.observe(element);
            }
          }
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});
